import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputBase,
  Typography,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import useScrollDirection from '../../hooks/useScrollDirection';
import LanguageSwitcher from './LanguageSwitcher';
import logo from './../../assets/logos/logo.webp';
import headerLogo from './../../assets/logos/logoHeader.webp';

const HEADER_HEIGHT = 72;
const HEADER_HEIGHT_COMPACT = 56;

const MINIMAL_NAV_PATHS = ['/empieza-aqui', '/get-started', '/commencer'];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(() => window.scrollY > 10);
  const [hasMounted, setHasMounted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const scrollDirection = useScrollDirection(80);
  const { t } = useTranslation('nav');
  const lp = useLocalizedPath();

  const isMinimal = MINIMAL_NAV_PATHS.some((p) => location.pathname.includes(p));

  const desktopNavItems = [
    { routeKey: 'creditCards', label: t('desktop.creditCards') },
    { routeKey: 'banking', label: t('desktop.banking') },
    { routeKey: 'mortgages', label: t('desktop.mortgages') },
    { routeKey: 'comparators', label: t('desktop.comparators') },
    { routeKey: 'tools', label: t('desktop.tools') },
    { routeKey: 'financlopedia', label: t('desktop.dictionary') },
    { routeKey: 'blog', label: t('desktop.guides') },
  ];

  const mobileNavItems = [
    { routeKey: 'home', label: t('mobile.home') },
    { routeKey: 'getStarted', label: t('mobile.getStarted') },
    { routeKey: 'creditCards', label: t('desktop.creditCards') },
    { routeKey: 'banking', label: t('desktop.banking') },
    { routeKey: 'mortgages', label: t('desktop.mortgages') },
    { routeKey: 'comparators', label: t('desktop.comparators') },
    { routeKey: 'tools', label: t('desktop.tools') },
    { routeKey: 'financlopedia', label: t('desktop.dictionary') },
    { routeKey: 'blog', label: t('desktop.guides') },
    { routeKey: 'contact', label: t('mobile.contact') },
  ];

  const utilityLinks = [
    { routeKey: 'howWeMonetize', label: t('utility.howWeRate') },
    { routeKey: 'howWeMonetize', label: t('utility.disclosure') },
  ];

  // Sync isScrolled on route change (ScrollToTop resets to 0)
  useEffect(() => {
    setIsScrolled(window.scrollY > 10);
  }, [location.pathname]);

  useEffect(() => {
    // Enable transitions only after first paint to prevent blink on load
    requestAnimationFrame(() => setHasMounted(true));

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleSearch = () => {
    setIsSearchOpen((prev) => !prev);
    if (isSearchOpen) setSearchQuery('');
  };
  const handleSearchSubmit = () => {
    const q = searchQuery.trim();
    if (!q) return;
    navigate(lp('financlopedia') + '?q=' + encodeURIComponent(q));
    setSearchQuery('');
    setIsSearchOpen(false);
  };
  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Skip-to-content link — visible only on keyboard focus */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: 9999,
          '&:focus': {
            position: 'fixed',
            top: 8,
            left: 8,
            width: 'auto',
            height: 'auto',
            px: 3,
            py: 1.5,
            bgcolor: '#1E3A5F',
            color: '#FFFFFF',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '14px',
            textDecoration: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        {t('a11y.skipToContent', 'Saltar al contenido principal')}
      </Box>

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E6E9EE',
          top: 0,
          zIndex: (t) => t.zIndex.appBar,
          transition: hasMounted ? 'height 0.2s ease, transform 0.3s ease' : 'none',
          /* Auto-hide on scroll — mobile only (CSS responsive) */
          transform: {
            xs: scrollDirection === 'down' ? 'translateY(-100%)' : 'none',
            md: 'none',
          },
        }}
      >
        {/* Utility row — always rendered, CSS-hidden on mobile + when scrolled */}
        <Box
          sx={{
            display: isScrolled ? 'none' : { xs: 'none', md: 'flex' },
            justifyContent: 'flex-end',
            alignItems: 'center',
            maxWidth: '1248px',
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 3 },
            height: '28px',
            borderBottom: '1px solid #E6E9EE',
          }}
        >
          {utilityLinks.map((link, i) => (
            <Box
              key={`${link.label}-${i}`}
              component={RouterLink}
              to={lp(link.routeKey)}
              sx={{
                textDecoration: 'none',
                color: '#5F6B7A',
                fontSize: '12px',
                fontWeight: 400,
                ml: '16px',
                lineHeight: '16px',
                '&:hover': { color: '#1E3A5F' },
              }}
            >
              {link.label}
            </Box>
          ))}
          <LanguageSwitcher variant="desktop" />
        </Box>

        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            height: {
              xs: `${HEADER_HEIGHT_COMPACT}px`,
              md: isScrolled ? `${HEADER_HEIGHT_COMPACT}px` : `${HEADER_HEIGHT}px`,
            },
            minHeight: {
              xs: `${HEADER_HEIGHT_COMPACT}px !important`,
              md: isScrolled ? `${HEADER_HEIGHT_COMPACT}px !important` : `${HEADER_HEIGHT}px !important`,
            },
            maxWidth: '1248px',
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 3 },
            transition: hasMounted ? 'height 0.2s ease, min-height 0.2s ease' : 'none',
          }}
        >
          {/* Mobile hamburger — always rendered, CSS-hidden on desktop; hidden on minimal pages */}
          {!isMinimal && (
            <IconButton
              aria-label={t('openMenu')}
              edge="start"
              onClick={toggleMenu}
              sx={{ color: '#1A1A1A', display: { xs: 'inline-flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo — centered on mobile via CSS, left-aligned on desktop */}
          <Box
            component={RouterLink}
            to={lp('home')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              flexShrink: 0,
              position: { xs: 'absolute', md: 'static' },
              left: { xs: '50%', md: 'auto' },
              transform: { xs: 'translateX(-50%)', md: 'none' },
            }}
          >
            {/* Mobile logo — hidden on desktop */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <img
                src={logo}
                alt={t('logoAlt')}
                width={102}
                height={34}
                style={{
                  height: isScrolled ? '32px' : '34px',
                  width: 'auto',
                  objectFit: 'contain',
                  transition: hasMounted ? 'height 0.2s ease' : 'none',
                }}
              />
            </Box>
            {/* Desktop logo — hidden on mobile */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <img
                src={headerLogo}
                alt={t('logoAlt')}
                width={126}
                height={42}
                style={{
                  height: isScrolled ? '32px' : '42px',
                  width: 'auto',
                  objectFit: 'contain',
                  transition: hasMounted ? 'height 0.2s ease' : 'none',
                }}
              />
            </Box>
          </Box>

          {/* Center nav — hidden on mobile and on minimal pages */}
          {!isMinimal && (
            <Box
              component="nav"
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                flexGrow: 1,
                gap: '24px',
                mx: 4,
              }}
            >
              {desktopNavItems.map((item) => {
                const path = lp(item.routeKey);
                return (
                  <NavLink
                    key={item.routeKey}
                    to={path}
                    label={item.label}
                    active={isActive(path)}
                  />
                );
              })}
            </Box>
          )}

          {/* Right: CTA + Search (hidden on minimal pages) */}
          {isMinimal ? (
            <Box sx={{ flexGrow: 1 }} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {/* CTA button — always rendered, CSS-hidden on mobile */}
              <Button
                component={RouterLink}
                to={lp('getStarted')}
                disableElevation
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  bgcolor: '#D32F2F',
                  color: '#fff',
                  borderRadius: '10px',
                  height: '36px',
                  px: 2,
                  fontSize: '13px',
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#B71C1C' },
                }}
              >
                {t('cta')}
              </Button>
              {/* CSS width instead of MUI Collapse — avoids hydration mismatch */}
              <Box sx={{
                overflow: 'hidden',
                width: isSearchOpen ? { xs: '160px', md: '200px' } : '0px',
                transition: 'width 0.2s ease',
              }}>
                <InputBase
                  id="nav-search"
                  name="search"
                  placeholder={t('search')}
                  autoFocus={isSearchOpen}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearchSubmit();
                    if (e.key === 'Escape') { setSearchQuery(''); setIsSearchOpen(false); }
                  }}
                  sx={{
                    height: '44px',
                    width: { xs: '160px', md: '200px' },
                    px: 2,
                    border: '1px solid #E6E9EE',
                    borderRadius: '10px',
                    fontSize: '14px',
                    backgroundColor: '#F7F9FC',
                    '&:focus-within': {
                      borderColor: '#1E3A5F',
                      backgroundColor: '#FFFFFF',
                    },
                  }}
                />
              </Box>
              <IconButton
                aria-label={isSearchOpen ? t('closeSearch') : t('search')}
                onClick={toggleSearch}
                sx={{
                  color: '#1A1A1A',
                  width: 44,
                  height: 44,
                }}
              >
                {isSearchOpen ? <CloseIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={isMenuOpen}
        onClose={toggleMenu}
        variant="temporary"
        sx={{
          '& .MuiDrawer-paper': {
            width: '80vw',
            maxWidth: '300px',
            backgroundColor: '#FFFFFF',
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            height: '56px',
            borderBottom: '1px solid #E6E9EE',
          }}
        >
          <RouterLink to={lp('home')} onClick={toggleMenu}>
            <Box
              component="img"
              src={logo}
              alt="FinMaple"
              sx={{ height: '32px', width: 'auto', objectFit: 'contain' }}
            />
          </RouterLink>
          <IconButton
            aria-label={t('closeMenu')}
            onClick={toggleMenu}
            sx={{ color: '#1A1A1A' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Nav links */}
        <List sx={{ width: '100%', py: 1 }}>
          {mobileNavItems.map((item) => {
            const path = lp(item.routeKey);
            return (
              <ListItem
                key={item.routeKey}
                component={RouterLink}
                to={path}
                onClick={toggleMenu}
                sx={{
                  py: 1.75,
                  px: 3,
                  minHeight: 48,
                  backgroundColor: isActive(path) ? '#F7F9FC' : 'transparent',
                  '&:hover': { backgroundColor: '#F7F9FC' },
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: isActive(path) ? '#1E3A5F' : '#1A1A1A',
                      fontWeight: isActive(path) ? 600 : 400,
                      fontSize: '15px',
                    },
                  }}
                />
              </ListItem>
            );
          })}
        </List>

        <Divider />

        {/* Language switcher in mobile drawer */}
        <Box sx={{ px: 3, py: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ color: '#5F6B7A', fontWeight: 500, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            {t('langSwitcher.label')}
          </Typography>
          <LanguageSwitcher variant="mobile" />
        </Box>

        <Divider />

        {/* Utility links in drawer */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography
            variant="caption"
            sx={{ color: '#5F6B7A', fontWeight: 500, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            {t('transparency')}
          </Typography>
          {utilityLinks.map((link, i) => (
            <Box
              key={`${link.label}-${i}`}
              component={RouterLink}
              to={lp(link.routeKey)}
              onClick={toggleMenu}
              sx={{
                display: 'block',
                textDecoration: 'none',
                color: '#1A1A1A',
                fontSize: '14px',
                py: 1,
              }}
            >
              {link.label}
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
};

const NavLink = ({ to, label, active }) => (
  <Box
    component={RouterLink}
    to={to}
    sx={{
      textDecoration: 'none',
      color: active ? '#1E3A5F' : '#1A1A1A',
      fontWeight: active ? 600 : 400,
      fontSize: '14px',
      whiteSpace: 'nowrap',
      transition: 'color 0.15s ease',
      lineHeight: '20px',
      '&:hover': { color: '#1E3A5F' },
    }}
  >
    {label}
  </Box>
);

export default Navbar;
